import { useWomBasketDetail } from '@/core/domain/wom/womBasket/womBasket.hooks';
import { useWorkorderDetailContext } from '../WorkorderDetailProvider';
import { Button, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import WomBasketCreateModal from './WomBasketCreateModal';
import WomBasketSyncModal from './WomBasketSyncModal';
import { useOAuth } from '@/core/libs/OAuth';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import useUrlParams from '@/hooks/useUrlParams';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useHasWomPermission } from '@/core/domain/wom/allowed-companies/allowed-companies.hooks';

type WOMType = 'create' | 'sync';

export default function WOM() {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const hasWOMPermission = useHasWomPermission(workorder?.company.id);

  const hasWomBasket = !!complement?.womBasketId;
  const isOpened = complement?.status === WorkOrderComplementStatus.Open;

  const { searchParams, setSearchParams, removeSearchParams } = useUrlParams<{
    WOMType?: string;
  }>();

  const WOMCredential = useOAuth('WOM');

  const womBasketDetail = useWomBasketDetail(
    complement?.id,
    hasWomBasket && hasWOMPermission,
  );

  const verifyWomAuth = useCallback(async () => {
    await WOMCredential.revalidate();
  }, [WOMCredential]);

  /**
   * Verifica se tem o search param 'WOMType'
   * para manipular a modal de cadastro e sincronização da cesta
   */
  const WOMTypeAction = useMemo(
    () => ({
      open: async (type?: WOMType) => {
        if (searchParams.WOMType !== type) {
          setSearchParams({ WOMType: type });
        }
        await verifyWomAuth();
      },
      close: () => {
        removeSearchParams(['WOMType']);
      },
    }),
    [searchParams.WOMType],
  );

  const WOMTypeOpened = useMemo(
    () => searchParams.WOMType,
    [searchParams.WOMType],
  );

  useEffect(() => {
    if (
      womBasketDetail.data &&
      !womBasketDetail.isLoading &&
      WOMTypeOpened === 'create'
    ) {
      removeSearchParams(['WOMType']);
    }
  }, [womBasketDetail, WOMTypeOpened]);

  if (!hasWOMPermission) return null;

  return (
    <>
      {WOMCredential.isLoading && (
        <ModalLoadingOverlay messages={['Validando acesso do WOM...']} />
      )}

      {!hasWomBasket && (
        <Tooltip
          title={!isOpened ? 'Disponível somente para orçamento aberto' : ''}
        >
          <Button
            type="primary"
            className="btn-bosch"
            onClick={() => WOMTypeAction.open('create')}
            disabled={!isOpened}
          >
            Criar cesta Bosch WOM
          </Button>
        </Tooltip>
      )}

      {hasWomBasket && (
        <Button
          type="primary"
          className="btn-bosch"
          onClick={() => WOMTypeAction.open('sync')}
        >
          Cesta Bosch WOM
        </Button>
      )}

      <WomBasketCreateModal
        opened={WOMTypeOpened === 'create' && isOpened}
        onClose={WOMTypeAction.close}
        onCreated={() => WOMTypeAction.open('sync')}
      />

      <WomBasketSyncModal
        opened={WOMTypeOpened === 'sync' && hasWomBasket}
        onClose={WOMTypeAction.close}
        basket={womBasketDetail.data}
      />
    </>
  );
}
